import React, { useState } from 'react';
import axios from 'axios';
import { Container, Row, Col } from 'react-bootstrap';
import './SpeakerForm.css';
import parse from "html-react-parser";

const SpeakerApplication = ({ webpageData }) => {
  // Extract speaker application data from JSON
  const speakerData = webpageData?.speakerApplicationSection || {};
  const formData_config = speakerData?.form || {};
  const fieldsConfig = formData_config?.fields || {};
  const modalsConfig = speakerData?.modals || {};
  const apiConfig = speakerData?.api || {};

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    title: '',
    company: '',
    email: '',
    speakingTopic: ''
  });

  const [formLoading, setFormLoading] = useState(false);
  const [validated, setValidated] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFailModal, setShowFailModal] = useState(false);
  
  // Individual field validation states
  const [fieldErrors, setFieldErrors] = useState({
    firstName: '',
    lastName: '',
    title: '',
    company: '',
    email: '',
    speakingTopic: ''
  });

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [id]: value
    }));

    // Clear error message when user starts typing
    if (fieldErrors[id]) {
      setFieldErrors(prevState => ({
        ...prevState,
        [id]: ''
      }));
    }
  };

  const validateField = (fieldName, value) => {
    const fieldConfig = fieldsConfig[fieldName];
    if (!fieldConfig) return '';

    const validationMessages = fieldConfig.validationMessages || {};

    switch (fieldName) {
      case 'firstName':
        if (!value.trim()) return validationMessages.required || 'Please provide your first name';
        if (value.trim().length < 2) return validationMessages.minLength || 'First name must be at least 2 characters';
        return '';
      
      case 'lastName':
        if (!value.trim()) return validationMessages.required || 'Please provide your last name';
        if (value.trim().length < 2) return validationMessages.minLength || 'Last name must be at least 2 characters';
        return '';
      
      case 'title':
        if (!value.trim()) return validationMessages.required || 'Please provide your title';
        return '';
      
      case 'company':
        if (!value.trim()) return validationMessages.required || 'Please provide your company name';
        return '';
      
      case 'email':
        if (!value.trim()) return validationMessages.required || 'Please provide your email address';
        const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(value.trim())) return validationMessages.invalid || 'Please provide a valid email address';
        return '';
      
      case 'speakingTopic':
        if (!value.trim()) return validationMessages.required || 'Please tell us what you would like to speak about';
        if (value.trim().length < 10) return validationMessages.minLength || 'Please provide more details about your speaking topic (at least 10 characters)';
        return '';
      
      default:
        return '';
    }
  };

  const validateAllFields = () => {
    const errors = {};
    let hasErrors = false;

    Object.keys(formData).forEach(field => {
      const error = validateField(field, formData[field]);
      errors[field] = error;
      if (error) hasErrors = true;
    });

    setFieldErrors(errors);
    return !hasErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setValidated(true);

    // Validate all fields
    if (!validateAllFields()) {
      return;
    }

    setFormLoading(true);

    try {
      const requestBody = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        address: formData.title.trim(),
        companyName: formData.company.trim(),
        email: formData.email.trim(),
        comments: formData.speakingTopic.trim(),
        referrerSource: apiConfig.referrerSource || "USSAIC-SPEAKER-2025",
        leadSource: apiConfig.leadSource || "USSAIC-SPEAKER-2025",
        TCPA_Consent_Given: true,
      };

      const response = await axios.post(
        process.env.REACT_APP_API_EMAIL,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json"
          },
          timeout: 30000
        }
      );


      setShowSuccessModal(true);
      resetForm();

    } catch (error) {

      setShowFailModal(true);
    } finally {
      setFormLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      title: '',
      company: '',
      email: '',
      speakingTopic: ''
    });
    setFieldErrors({
      firstName: '',
      lastName: '',
      title: '',
      company: '',
      email: '',
      speakingTopic: ''
    });
    setValidated(false);
  };

  const handleSuccessModalClose = () => setShowSuccessModal(false);
  const handleFailModalClose = () => setShowFailModal(false);

  return (
    <div className="ussaic-speaker-application">
      {/* Success Modal */}
      {showSuccessModal && (
        <div className="ussaic-modal-overlay">
          <div className="ussaic-modal-content">
            <div className="ussaic-modal-header">
              <button onClick={handleSuccessModalClose} className="ussaic-modal-close-button">×</button>
            </div>
            <div className="ussaic-modal-body">
              <div className="ussaic-success-icon">
                {modalsConfig.success?.icon || '✓'}
              </div>
              <h4>{modalsConfig.success?.title || 'Thank You!'}</h4>
              <p>{parse(modalsConfig.success?.message || 'Your speaker application has been successfully submitted. We\'ll review your application and get back to you soon.')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showFailModal && (
        <div className="ussaic-modal-overlay">
          <div className="ussaic-modal-content">
            <div className="ussaic-modal-header">
              <button onClick={handleFailModalClose} className="ussaic-modal-close-button">×</button>
            </div>
            <div className="ussaic-modal-body">
              <div className="ussaic-error-icon">
                {modalsConfig.error?.icon || '✕'}
              </div>
              <h4>{modalsConfig.error?.title || 'Submission Failed'}</h4>
              <p>{parse(modalsConfig.error?.message || 'We\'re sorry, but we couldn\'t process your application at this time. Please try again later or contact us directly.')}</p>
            </div>
          </div>
        </div>
      )}

      <Container className="ussaic-content-container">
        <Row>
          <Col xs={12} sm={12} md={5} lg={5} xl={5} xxl={5} className="ussaic-left-content">
            <div className="ussaic-apply-text">
              {speakerData.tagline || 'APPLY TO SPEAK'}
            </div>
            <h1 className="ussaic-heading">
              {parse(speakerData.heading || 'Speak at the First Annual Congressional Exchange')}
            </h1>
            <p className="ussaic-description">
              {parse(speakerData.description || 'Share your vision and expertise with a focused audience of industry leaders and innovators.')}
            </p>
            <p className="ussaic-invitation">
              {parse(speakerData.invitation || 'We invite you to submit your speaking proposal and become a vital part of US Strategy for AI & Crypto (USSAIC).')}
            </p>
          </Col>
          <Col xs={12} sm={12} md={7} lg={7} xl={7} xxl={7} className="ussaic-right-content">
            <form onSubmit={handleSubmit} noValidate className="ussaic-form">
              <div className="ussaic-form-row">
                <div className="ussaic-form-group">
                  <input
                    type="text"
                    id="firstName"
                    placeholder={fieldsConfig.firstName?.placeholder || 'FIRST NAME'}
                    value={formData.firstName}
                    onChange={handleChange}
                    required={fieldsConfig.firstName?.required}
                    className={`ussaic-input ${fieldErrors.firstName ? 'ussaic-input-error' : ''}`}
                  />
                  {fieldErrors.firstName && (
                    <div className="ussaic-error-message">{fieldErrors.firstName}</div>
                  )}
                </div>
                <div className="ussaic-form-group">
                  <input
                    type="text"
                    id="lastName"
                    placeholder={fieldsConfig.lastName?.placeholder || 'LAST NAME'}
                    value={formData.lastName}
                    onChange={handleChange}
                    required={fieldsConfig.lastName?.required}
                    className={`ussaic-input ${fieldErrors.lastName ? 'ussaic-input-error' : ''}`}
                  />
                  {fieldErrors.lastName && (
                    <div className="ussaic-error-message">{fieldErrors.lastName}</div>
                  )}
                </div>
              </div>
              <div className="ussaic-form-row">
                <div className="ussaic-form-group">
                  <input
                    type="text"
                    id="title"
                    placeholder={fieldsConfig.title?.placeholder || 'TITLE'}
                    value={formData.title}
                    onChange={handleChange}
                    required={fieldsConfig.title?.required}
                    className={`ussaic-input ${fieldErrors.title ? 'ussaic-input-error' : ''}`}
                  />
                  {fieldErrors.title && (
                    <div className="ussaic-error-message">{fieldErrors.title}</div>
                  )}
                </div>
                <div className="ussaic-form-group">
                  <input
                    type="text"
                    id="company"
                    placeholder={fieldsConfig.company?.placeholder || 'COMPANY'}
                    value={formData.company}
                    onChange={handleChange}
                    required={fieldsConfig.company?.required}
                    className={`ussaic-input ${fieldErrors.company ? 'ussaic-input-error' : ''}`}
                  />
                  {fieldErrors.company && (
                    <div className="ussaic-error-message">{fieldErrors.company}</div>
                  )}
                </div>
              </div>
              <div className="ussaic-form-group ussaic-full-width">
                <input
                  type="email"
                  id="email"
                  placeholder={fieldsConfig.email?.placeholder || 'EMAIL'}
                  value={formData.email}
                  onChange={handleChange}
                  required={fieldsConfig.email?.required}
                  className={`ussaic-input ${fieldErrors.email ? 'ussaic-input-error' : ''}`}
                />
                {fieldErrors.email && (
                  <div className="ussaic-error-message">{fieldErrors.email}</div>
                )}
              </div>
              <div className="ussaic-form-group ussaic-full-width">
                <textarea
                  id="speakingTopic"
                  placeholder={fieldsConfig.speakingTopic?.placeholder || 'PLEASE TELL US A LITTLE ABOUT WHAT YOU WOULD LIKE TO SPEAK ABOUT...'}
                  value={formData.speakingTopic}
                  onChange={handleChange}
                  required={fieldsConfig.speakingTopic?.required}
                  className={`ussaic-textarea ${fieldErrors.speakingTopic ? 'ussaic-input-error' : ''}`}
                />
                {fieldErrors.speakingTopic && (
                  <div className="ussaic-error-message">{fieldErrors.speakingTopic}</div>
                )}
              </div>
              <div className="ussaic-submit-container">
                <button type="submit" className="ussaic-submit-btn" disabled={formLoading}>
                  {formLoading 
                    ? (formData_config.submitButton?.loadingText || "PLEASE WAIT...") 
                    : (formData_config.submitButton?.text || "SUBMIT")
                  }
                </button>
              </div>
            </form>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default SpeakerApplication;